export type FormErrors = Record<string, any>;

/**
 * Scrolls to the first field that has a validation error.
 * Attempts multiple strategies to locate the relevant DOM element.
 */
export function scrollToFirstError(
  errors: FormErrors,
  formSelector: string = "form"
): void {
  if (!errors || typeof document === "undefined") return;

  const firstErrorField = Object.keys(errors)[0];
  if (!firstErrorField) return;

  let fieldElement: Element | null = null;

  // Strategy 1: by name
  fieldElement = document.querySelector(`[name="${firstErrorField}"]`);

  // Strategy 2: by id
  if (!fieldElement) {
    fieldElement = document.querySelector(`#${firstErrorField}`);
  }

  // Strategy 3: by label association or proximity
  if (!fieldElement) {
    const labels = Array.from(document.querySelectorAll("label"));
    const matchingLabel = labels.find(
      (label) =>
        label.textContent
          ?.toLowerCase()
          .includes(firstErrorField.toLowerCase()) ||
        label.getAttribute("for") === firstErrorField
    );
    if (matchingLabel) {
      const inputId = matchingLabel.getAttribute("for");
      if (inputId) {
        fieldElement = document.getElementById(inputId);
      }
      if (!fieldElement && matchingLabel.parentElement) {
        fieldElement = matchingLabel.parentElement.querySelector(
          'input, select, textarea, [role="combobox"]'
        );
      }
    }
  }

  // Strategy 4: by common field patterns within grouped containers
  if (!fieldElement) {
    const allInputs = Array.from(
      document.querySelectorAll('input, select, textarea, [role="combobox"]')
    );
    for (const input of allInputs) {
      const label = input
        .closest(".col, .form-group, .mb-3")
        ?.querySelector("label");
      if (
        label &&
        label.textContent?.toLowerCase().includes(firstErrorField.toLowerCase())
      ) {
        fieldElement = input;
        break;
      }
    }
  }

  // Strategy 5: any element marked with error classes
  if (!fieldElement) {
    fieldElement = document.querySelector(
      '.is-invalid, .error, [class*="error"], .text-danger'
    );
  }

  // Strategy 6: element near visible error message
  if (!fieldElement) {
    const errorMessages = document.querySelectorAll(
      '[class*="error"], .text-danger'
    );
    if (errorMessages.length > 0) {
      const firstErrorMessage = errorMessages[0];
      const container = firstErrorMessage.closest(
        ".form-group, .mb-3, .col, .row"
      );
      if (container) {
        fieldElement = container.querySelector(
          'input, select, textarea, [role="combobox"]'
        );
      }
    }
  }

  if (fieldElement) {
    fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
    if (
      fieldElement instanceof HTMLElement &&
      typeof fieldElement.focus === "function"
    ) {
      setTimeout(() => {
        try {
          fieldElement?.focus();
        } catch {
          /* noop */
        }
      }, 300);
    }
  } else {
    const formElement = document.querySelector(formSelector);
    formElement?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}
